import React, { useState, useEffect } from 'react';
    import { motion } from 'framer-motion';
    import smsService from '../services/smsService';
    import SafeIcon from '../common/SafeIcon';
    import * as FiIcons from 'react-icons/fi';

    const { FiX, FiSave, FiMessageSquare, FiLock, FiCheck, FiAlertTriangle, FiPhone } = FiIcons;

    const SmsSettingsModal = ({ onClose }) => {
      const [formData, setFormData] = useState({ accountSid: '', authToken: '', fromNumber: '', });
      const [loading, setLoading] = useState(false);
      const [testing, setTesting] = useState(false);
      const [testResult, setTestResult] = useState(null);
      const [testPhoneNumber, setTestPhoneNumber] = useState('');

      useEffect(() => {
        loadExistingConfig();
      }, []);

      const loadExistingConfig = async () => {
        try {
          const config = await smsService.getTwilioConfig();
          if (config) {
            setFormData({
              accountSid: config.accountSid || '',
              authToken: config.authToken || '',
              fromNumber: config.fromNumber || '',
            });
          }
        } catch (error) {
          console.error('Error loading Twilio config:', error);
        }
      };

      const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
      };

      const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
          const result = await smsService.saveTwilioConfig(formData);
          if (result.success) {
            alert('Twilio configuration saved successfully!');
            onClose();
          } else {
            alert(`Error: ${result.message}`);
          }
        } catch (error) {
          console.error('Error saving Twilio config:', error);
          alert('Failed to save Twilio configuration');
        } finally {
          setLoading(false);
        }
      };

      const handleTestSms = async () => {
        if (!testPhoneNumber) {
          alert('Please enter a phone number to send a test SMS to.');
          return;
        }
        setTesting(true);
        setTestResult(null);
        try {
          // First save the current config
          await smsService.saveTwilioConfig(formData);
          // Then send test SMS (simulation)
          const result = await smsService.sendSms({
            to: testPhoneNumber,
            body: `Test SMS from ExpertGuru. Your Twilio configuration is working.`,
            type: 'test',
          });
          if (result.success) {
            setTestResult({ success: true, message: 'Test SMS logged for sending by backend.' });
          } else {
            setTestResult({ success: false, message: result.message });
          }
        } catch (error) {
          console.error('Error sending test SMS:', error);
          setTestResult({ success: false, message: error.message });
        } finally {
          setTesting(false);
        }
      };

      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">SMS Settings (Twilio)</h2>
                <p className="text-sm text-gray-600 mt-1">Configure Twilio to send SMS notifications.</p>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors"><SafeIcon icon={FiX} className="text-xl" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <SafeIcon icon={FiAlertTriangle} className="text-yellow-500 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-yellow-900">Backend Requirement</h4>
                    <p className="text-sm text-yellow-800 mt-1">
                      This form saves your Twilio credentials. Actually sending SMS requires a backend service (e.g., Firebase Functions) to use these credentials securely. Exposing them in the browser is unsafe.
                    </p>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Twilio Credentials</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Account SID *</label>
                    <input type="text" value={formData.accountSid} onChange={(e) => handleChange('accountSid', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none" placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Auth Token *</label>
                    <input type="password" value={formData.authToken} onChange={(e) => handleChange('authToken', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none" placeholder="Your Twilio Auth Token" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Twilio Phone Number *</label>
                    <input type="tel" value={formData.fromNumber} onChange={(e) => handleChange('fromNumber', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none" placeholder="+15017122661" required />
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Test Configuration</h4>
                <div className="flex gap-3">
                  <input type="tel" value={testPhoneNumber} onChange={(e) => setTestPhoneNumber(e.target.value)} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none" placeholder="Enter phone number to send test SMS" />
                  <button type="button" onClick={handleTestSms} disabled={testing || !testPhoneNumber} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2">
                    {testing ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Testing...</>) : (<><SafeIcon icon={FiMessageSquare} /> Send Test</>)}
                  </button>
                </div>
                {testResult && (
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className={`mt-3 p-3 rounded-lg border ${testResult.success ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
                    <div className="flex items-center gap-2"><SafeIcon icon={testResult.success ? FiCheck : FiAlertTriangle} /> <span className="text-sm font-medium">{testResult.success ? 'Success!' : 'Failed!'}</span></div>
                    <p className="text-sm mt-1">{testResult.message}</p>
                  </motion.div>
                )}
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button type="button" onClick={onClose} className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">Cancel</button>
                <motion.button whileHover={{ scale: loading ? 1 : 1.05 }} whileTap={{ scale: loading ? 1 : 0.95 }} type="submit" disabled={loading} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  {loading ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Saving...</>) : (<><SafeIcon icon={FiSave} /> Save Configuration</>)}
                </motion.button>
              </div>
            </form>
          </motion.div>
        </div>
      );
    };
    export default SmsSettingsModal;